/**
 * @providesModule UserPropertiesHelpers
 */

import {ORDERED_CONVERSIONS_STEPS} from 'GameOperationsTutorialController';
import {LeagueFTUEConstants} from 'LeagueFTUEController';
import Preferences from 'Preferences';
import SKZAPIConstants from 'SKZAPIConstants';
import SKZConstants from 'SKZConstants';
import {
  AllUserPropertiesKeys,
  FTUE_ASYNC_BRACKETS_KEY,
  FTUE_GLOBAL_LEAGUES_MS1_KEY,
  FTUE_INTERSTITIAL_KEY,
  FTUE_LEVELS_KEY,
  FTUE_PLAY_AGAIN_KEY,
  FTUE_PROGRESSIVE_LEAGUES_KEY,
  FTUE_PROGRESSIVE_LEAGUES_V2_KEY,
  FTUE_PROGRESSIVE_LEAGUES_V2_MILESTONE_KEY,
  FTUE_STORE_KEY,
  FTUE_TROPHIES_KEY,
  FtueKeys,
} from 'UserPropertiesConstants';
import {UserPropertyCategories, getUserPropertiesByCategory} from 'UserPropertiesController';

/**
 * Helper function to get the server FTUE user properties as a single object. This is used to sync the local and server values with an O(n) complexity.
 * @returns Server user properties as a single object.
 */
export const getServerFtueUserPropertiesAsObject = async () => {
  const serverPropertiesArray = await getUserPropertiesByCategory('ftue');

  const serverPropertiesObj: {[key in AllUserPropertiesKeys]?: string} = {};

  if (!serverPropertiesArray) {
    return serverPropertiesObj;
  }

  serverPropertiesArray.forEach((property) => {
    serverPropertiesObj[property.key] = property.value;
  });

  return serverPropertiesObj;
};

/**
 * Helper function to get the local FTUE user properties as an array of objects that have their values set to a boolean. This is used to sync the local and server values with an O(n) complexity.
 * @returns Local user properties as an array of objects, or null if there are no properties.
 */
export const getLocalFtueUserProperties = (): {key: FtueKeys; value: boolean}[] => {
  const currentStepIndexInterstitial =
    +Preferences.get(mapServerKeysToLocalValue(FTUE_INTERSTITIAL_KEY)) || 0;

  // We force everything to boolean because on iOS, "true" and "false" are 1s and 0s, while on android they are true and false.
  return [
    {
      key: FTUE_STORE_KEY,
      value: Boolean(Preferences.get(mapServerKeysToLocalValue(FTUE_STORE_KEY))),
    },
    {
      key: FTUE_PROGRESSIVE_LEAGUES_KEY,
      value: Boolean(Preferences.get(mapServerKeysToLocalValue(FTUE_PROGRESSIVE_LEAGUES_KEY))),
    },
    {
      key: FTUE_GLOBAL_LEAGUES_MS1_KEY,
      value: Boolean(Preferences.get(mapServerKeysToLocalValue(FTUE_GLOBAL_LEAGUES_MS1_KEY))),
    },
    {
      key: FTUE_PROGRESSIVE_LEAGUES_V2_KEY,
      value: Boolean(Preferences.get(mapServerKeysToLocalValue(FTUE_PROGRESSIVE_LEAGUES_V2_KEY))),
    },
    {
      key: FTUE_PROGRESSIVE_LEAGUES_V2_MILESTONE_KEY,
      value: Boolean(
        Preferences.get(mapServerKeysToLocalValue(FTUE_PROGRESSIVE_LEAGUES_V2_MILESTONE_KEY))
      ),
    },
    {
      key: FTUE_TROPHIES_KEY,
      value: Boolean(Preferences.get(mapServerKeysToLocalValue(FTUE_TROPHIES_KEY))),
    },
    {
      key: FTUE_PLAY_AGAIN_KEY,
      value: Boolean(Preferences.get(mapServerKeysToLocalValue(FTUE_PLAY_AGAIN_KEY))),
    },
    {
      key: FTUE_INTERSTITIAL_KEY,
      value: Boolean(currentStepIndexInterstitial === ORDERED_CONVERSIONS_STEPS.length - 1), // this property is an index value, need to compare it to the final step to know if the user has completed the FTUE
    },
    {
      key: FTUE_LEVELS_KEY,
      value: Boolean(Preferences.get(mapServerKeysToLocalValue(FTUE_LEVELS_KEY))),
    },
    {
      key: FTUE_ASYNC_BRACKETS_KEY,
      value: getTransformedAsyncBracketsValue(),
    },
  ];
};

/**
 * Helper function to map the server keys to the local keys.
 * @param key The server key to map.
 * @returns The mapped local key.
 */
export const mapServerKeysToLocalValue = (key: FtueKeys): string => {
  switch (key) {
    case FTUE_STORE_KEY:
      return SKZConstants.SKZ_HAS_SEEN_STORE_TUTORIAL_KEY;
    case FTUE_PROGRESSIVE_LEAGUES_KEY:
      return LeagueFTUEConstants.PROGRESSIVE_LEAGUES_TUTORIAL_SEEN;
    case FTUE_GLOBAL_LEAGUES_MS1_KEY:
      return LeagueFTUEConstants.GLOBAL_LEAGUES_MS1_TUTORIAL_SEEN;
    case FTUE_PROGRESSIVE_LEAGUES_V2_KEY:
      return LeagueFTUEConstants.PROGRESSIVE_LEAGUES_V2_TUTORIAL_SEEN;
    case FTUE_PROGRESSIVE_LEAGUES_V2_MILESTONE_KEY:
      return LeagueFTUEConstants.PROGRESSIVE_LEAGUES_V2_MILESTONE_TUTORIAL_SEEN;
    case FTUE_TROPHIES_KEY:
      return SKZConstants.SKZ_HAS_SEEN_TROPHY_INFO_MODAL;
    case FTUE_PLAY_AGAIN_KEY:
      return SKZConstants.SKZ_SHOULD_SKIP_PLAY_AGAIN_INFO;
    case FTUE_INTERSTITIAL_KEY:
      return SKZConstants.SKZ_GAME_OPERATIONS_TUTORIAL_STEP_INDEX;
    case FTUE_LEVELS_KEY:
      return SKZConstants.SKZ_HAS_SEEN_HOW_TO_LEVEL_UP_MODAL;
    case FTUE_ASYNC_BRACKETS_KEY:
    default:
      return SKZAPIConstants.SKZ_SHOW_BRACKET_WARNING;
  }
};

/**
 * The async brackets local storage flag is different than the others because it represents "show
 * warning", not "has seen warning". We need to transform the value we get from here to look
 * like the other FTUE flags, where "undefined or falsy" means "has not seen the FTUE" and "truthy" means "has seen the FTUE".
 * @returns boolean
 */
export const getTransformedAsyncBracketsValue = () => {
  const shouldShowWarning = Preferences.get(mapServerKeysToLocalValue(FTUE_ASYNC_BRACKETS_KEY));
  if (shouldShowWarning === undefined) {
    return false;
  }
  return !Boolean(shouldShowWarning);
};

/**
 * Helper function to set the local FTUE user properties correctly to their representation of "this FTUE has been completed".
 * @param key The server key of the user property to update.
 */
export const setLocalFtueUserPropertiesForTrueServerValue = (key: FtueKeys) => {
  if (key === FTUE_INTERSTITIAL_KEY) {
    // This step is checked by index value, not boolean value
    Preferences.set({[mapServerKeysToLocalValue(key)]: ORDERED_CONVERSIONS_STEPS.length - 1});
  } else if (key === FTUE_ASYNC_BRACKETS_KEY) {
    // This step represents "should we show the warning", not "has seen the warning", so we need to set it to false
    Preferences.set({[mapServerKeysToLocalValue(key)]: false});
  } else if (key === FTUE_PROGRESSIVE_LEAGUES_KEY) {
    // If the user has seen the progressive leagues FTUE, we need to set the other leagues FTUEs to true as well to "fully" mark Progressive Leagues FTUE as complete
    Preferences.set({[mapServerKeysToLocalValue(key)]: true});
    Preferences.set({[SKZConstants.SKZ_HAS_SEEN_LEADERBOARDS_UNLOCKED]: true}); // this controls the lock icon above the leagues tab
    Preferences.set({[LeagueFTUEConstants.LEAGUE_TAB_TOOLTIP_SEEN]: true}); // this controls the masked view pointing to the leagues tab after it has been unlocked
  } else if (key === FTUE_PROGRESSIVE_LEAGUES_V2_KEY) {
    // If the user has seen the progressive leagues v2 FTUE, we need to set the other leagues FTUEs to true as well to "fully" mark Progressive Leagues V2 FTUE as complete. This includes ProgressiveLeagues V1 FTUE(s). Not including milestone FTUE, because it can be triggered separately from the main FTUE once a user opts into the paid league.
    Preferences.set({[mapServerKeysToLocalValue(key)]: true});
    setLocalFtueUserPropertiesForTrueServerValue(FTUE_PROGRESSIVE_LEAGUES_KEY);
    setLocalFtueUserPropertiesForTrueServerValue(FTUE_GLOBAL_LEAGUES_MS1_KEY);
  } else {
    // All other steps are "has seen the FTUE", so we set them to true
    Preferences.set({[mapServerKeysToLocalValue(key)]: true});
  }
};

/**
 * Helper function to get the server user properties as a record.
 * @param category The category of user properties to retrieve.
 * @returns Server user properties as a record of key-value pairs.
 *
 * @example
 * const serverProperties = await getServerFtueUserPropertiesAsRecord('withdrawal');
 */
export const getServerUserPropertiesAsRecord = async <T extends AllUserPropertiesKeys>(
  category: UserPropertyCategories
) => {
  const serverPropertiesArray = await getUserPropertiesByCategory(category);

  if (!serverPropertiesArray?.length) {
    return {} as Record<T, string>;
  }

  return serverPropertiesArray.reduce((acc, property) => {
    return {
      ...acc,
      [property.key]: property.value,
    };
  }, {}) as Record<T, string>;
};
